import { FC, RefObject, useState } from 'react';

import multicreationBidChangeAllFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_bid_change_all_form_submit';
import multicreationBudgetChangeAllFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_budget_change_all_form_submit';
import { Cell, CellText, HSpacing, Radio, Text, useBreakpoint, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';
import { ChevronWide3OutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import { useMultiCreationBidBudgetValidation } from 'hooks/multiCreation/useMultiCreationBidBudgetValidation';
import useTrls from 'hooks/translation/useTrls';

import { MoneyInput } from 'components/common/MoneyInput/MoneyInput';
import { fetchBatchBidRating } from 'components/MultiCreationAuction/api/fetchBatchBidRating';
import { ChangeAllDrop } from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableHeader/components/ChangeAllDrop';

import {
    AuctionCreateFieldKey,
    selectMultiCreationBidFrequencyStatusLoading,
    selectMultiCreationVacanciesAuctionInfo,
    setMultiCreationBidsToRecommended,
    setMultiCreationBudgetsToRecommended,
    updateMultiCreationField,
} from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableHeader/components/ChangeAllBidsBudgetDrop/ChangeAllBidsBudgetDrop.module.less';

interface ChangeAllBidsBudgetDropProps {
    activatorRef: RefObject<HTMLElement>;
    isVisible: boolean;
    closeDrop: () => void;
    fieldKey: AuctionCreateFieldKey.Bid | AuctionCreateFieldKey.Budget;
}

const sendAnalyticsOnApply = (
    fieldKey: AuctionCreateFieldKey.Bid | AuctionCreateFieldKey.Budget,
    checkedRadioId: 'auto' | 'manual',
    value: string,
    validationError?: string
) => {
    if (fieldKey === AuctionCreateFieldKey.Bid) {
        multicreationBidChangeAllFormSubmit({ radioType: checkedRadioId, bidAmount: value, validationError });
    }
    if (fieldKey === AuctionCreateFieldKey.Budget) {
        multicreationBudgetChangeAllFormSubmit({ radioType: checkedRadioId, budgetAmount: value, validationError });
    }
};

const TrlKeys = {
    currency: 'currency.short',
    bid: {
        title: 'MultiCreationAuction.ChangeAllDrop.bid.title',
        mobileTitle: 'MultiCreationAuction.ChangeAllDrop.bid.mobileTitle',
        placeholder: 'MultiCreationAuction.ChangeAllDrop.bid.placeholder',
        radioAuto: 'MultiCreationAuction.ChangeAllDrop.bid.radio.auto',
        radioManual: 'MultiCreationAuction.ChangeAllDrop.bid.radio.manual',
    },
    budget: {
        title: 'MultiCreationAuction.ChangeAllDrop.budget.title',
        mobileTitle: 'MultiCreationAuction.ChangeAllDrop.budget.mobileTitle',
        placeholder: 'MultiCreationAuction.ChangeAllDrop.budget.placeholder',
        radioAuto: 'MultiCreationAuction.ChangeAllDrop.budget.radio.auto',
        radioManual: 'MultiCreationAuction.ChangeAllDrop.budget.radio.manual',
    },
};

export const ChangeAllBidsBudgetDrop: FC<ChangeAllBidsBudgetDropProps> = ({
    activatorRef,
    isVisible,
    closeDrop,
    fieldKey,
}) => {
    const vacanciesAuctionInfo = useSelector(selectMultiCreationVacanciesAuctionInfo);
    const auctionsToCreate = useSelector(({ multiCreation }) => multiCreation.auctionsToCreate);

    const isBidStatusLoading = useSelector(selectMultiCreationBidFrequencyStatusLoading);
    const biddingStrategy = useSelector(({ multiCreation }) => multiCreation.biddingStrategy);
    const minValue =
        fieldKey === AuctionCreateFieldKey.Bid
            ? Object.values(auctionsToCreate)[0].minBidValue
            : Object.values(vacanciesAuctionInfo)[0][fieldKey].minValue;

    const [checkedRadioId, setCheckedRadioId] = useState<'auto' | 'manual'>('auto');
    const [value, setValue] = useState(String(minValue));
    const [error, setError] = useState<string | null>(null);

    const { isMobile } = useBreakpoint();
    const trls = useTrls();
    const dispatch = useDispatch();
    const { validateBid, validateBudget } = useMultiCreationBidBudgetValidation({ biddingStrategy });

    const handleApplyClick = () => {
        if (checkedRadioId === 'auto') {
            if (fieldKey === AuctionCreateFieldKey.Bid) {
                dispatch(setMultiCreationBidsToRecommended());
            }
            if (fieldKey === AuctionCreateFieldKey.Budget) {
                dispatch(setMultiCreationBudgetsToRecommended());
            }
            sendAnalyticsOnApply(fieldKey, checkedRadioId, value);
        } else {
            let validationError = null;
            if (fieldKey === AuctionCreateFieldKey.Bid) {
                validationError = validateBid(value, minValue);
            }
            if (fieldKey === AuctionCreateFieldKey.Budget) {
                validationError = validateBudget(value, minValue);
            }
            if (validationError) {
                setError(validationError);
                sendAnalyticsOnApply(fieldKey, checkedRadioId, value, validationError);
                return;
            }
            sendAnalyticsOnApply(fieldKey, checkedRadioId, value);
            dispatch(updateMultiCreationField({ fieldKey, newValue: value }));
            setCheckedRadioId('auto');

            if (fieldKey === AuctionCreateFieldKey.Bid) {
                void dispatch(fetchBatchBidRating(Number(value)));
            }
        }

        closeDrop();
    };

    const handleChange = (value: string) => {
        setValue(value);
        if (fieldKey === AuctionCreateFieldKey.Bid) {
            setError(validateBid(value, minValue));
        }
        if (fieldKey === AuctionCreateFieldKey.Budget) {
            setError(validateBudget(value, minValue));
        }
    };

    return (
        <ChangeAllDrop
            activatorRef={activatorRef}
            bottomSheetTitle={trls(TrlKeys[fieldKey].mobileTitle)}
            closeDrop={closeDrop}
            onApply={handleApplyClick}
            visible={isVisible}
            disabled={fieldKey === AuctionCreateFieldKey.Bid && isBidStatusLoading}
        >
            <VSpacingContainer default={16}>
                {!isMobile && <Text typography="subtitle-1-semibold">{trls(TrlKeys[fieldKey].title)}</Text>}
                <Cell
                    left={
                        <Radio
                            checked={checkedRadioId === 'auto'}
                            name="auto"
                            value="auto"
                            id="auto"
                            onChange={() => setCheckedRadioId('auto')}
                        />
                    }
                    data-qa="change-all-bids-budget-drop-radio-auto"
                >
                    <div className={styles.radioItemAuto}>
                        <CellText>{trls(TrlKeys[fieldKey].radioAuto)}</CellText>
                        {fieldKey === AuctionCreateFieldKey.Bid && (
                            <>
                                <HSpacing default={12} />
                                <ChevronWide3OutlinedSize24 initialColor="positive-secondary" />
                            </>
                        )}
                    </div>
                </Cell>
                <Cell
                    left={
                        <Radio
                            checked={checkedRadioId === 'manual'}
                            name="manual"
                            value="manual"
                            id="manual"
                            onChange={() => setCheckedRadioId('manual')}
                        />
                    }
                    data-qa="change-all-bids-budget-drop-radio-manual"
                >
                    <CellText>{trls(TrlKeys[fieldKey].radioManual)}</CellText>
                </Cell>
            </VSpacingContainer>
            {checkedRadioId === 'manual' && (
                <>
                    <VSpacing default={24} />
                    <MoneyInput
                        allowNegative={false}
                        decimalMark={[]}
                        decimalLength={0}
                        postfix={trls(TrlKeys.currency)}
                        size="medium"
                        placeholder={trls(TrlKeys[fieldKey].placeholder)}
                        onChange={handleChange}
                        value={value}
                        invalid={!!error}
                        errorMessage={error || undefined}
                        disabled={fieldKey === AuctionCreateFieldKey.Bid && isBidStatusLoading}
                    />
                </>
            )}
        </ChangeAllDrop>
    );
};
